import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Lock, ArrowRight } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { useIsStoreApp } from "@/lib/storeAppDetection";

const STORAGE_KEY = "pawlink_downgrade_banner_dismissed";
const easeOut = [0.16, 1, 0.3, 1];

/**
 * Warm, non-punitive downgrade banner.
 *
 * Rendered on Home/Dashboard for users whose Paddle subscription ended
 * (subscription_status in canceled/paused/past_due) but who are now back on
 * the free plan. Copy is: "Luna's basic rescue profile is still active ❤️".
 * Never blocks the app, always dismissible.
 */
export default function DowngradedBanner() {
    const { user } = useAuth();
    const { t } = useI18n();
    const nav = useNavigate();
    const isStoreApp = useIsStoreApp();
    const [visible, setVisible] = useState(false);
    const [petName, setPetName] = useState("");

    useEffect(() => {
        if (!user || user.plan === "premium") return;
        let cancelled = false;

        (async () => {
            try {
                const [{ data: status }, { data: pets }] = await Promise.all([
                    api.get("/paddle/status"),
                    api.get("/pets"),
                ]);
                if (cancelled) return;

                const wasSubscribed = ["canceled", "paused", "past_due", "canceled_pending"].includes(
                    status?.subscription_status,
                );
                if (!wasSubscribed) return;

                const dismissed = localStorage.getItem(STORAGE_KEY);
                if (dismissed) return;

                const firstPet = Array.isArray(pets) && pets.length ? pets[0] : null;
                setPetName(firstPet?.name || t("downgrade.default_pet"));
                setVisible(true);
            } catch { /* passive */ }
        })();
        return () => { cancelled = true; };
    }, [user, t]);

    const dismiss = () => {
        try { localStorage.setItem(STORAGE_KEY, new Date().toISOString()); } catch { /* noop */ }
        setVisible(false);
    };

    const upgrade = () => {
        dismiss();
        nav("/subscription");
    };

    if (!visible) return null;

    return (
        <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: easeOut }}
            data-testid="downgrade-banner"
            className="relative mx-auto max-w-4xl mt-4 sm:mt-6 px-4"
        >
            <div className="relative rounded-2xl border border-paw-terra/20 bg-gradient-to-r from-paw-terra/8 via-white/[0.03] to-emerald-500/8 backdrop-blur px-4 py-3 sm:px-5 sm:py-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className="w-9 h-9 rounded-full bg-paw-terra/15 ring-1 ring-paw-terra/25 flex items-center justify-center flex-shrink-0">
                        <ShieldCheck size={16} weight="fill" className="text-paw-terra" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <p data-testid="downgrade-banner-title" className="text-[14px] sm:text-[15px] font-semibold text-white leading-tight">
                            {t("downgrade.title").replace("{pet}", petName)}
                        </p>
                        <p data-testid="downgrade-banner-sub" className="mt-0.5 text-[12px] sm:text-[13px] text-white/60 leading-snug flex items-center gap-1.5">
                            <Lock size={11} weight="fill" className="text-white/40" />
                            <span>{t("downgrade.sub")}</span>
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                    {/* Google Play compliance: no upgrade CTA in store-app
                        builds. Users can still dismiss the informational
                        banner. */}
                    {!isStoreApp && (
                        <button
                            type="button"
                            onClick={upgrade}
                            data-testid="downgrade-banner-cta"
                            className="inline-flex items-center gap-1.5 rounded-full bg-paw-terra hover:bg-paw-terraDark text-white px-4 py-2 text-[13px] font-semibold transition-all duration-300 hover:-translate-y-0.5"
                        >
                            {t("downgrade.cta")}
                            <ArrowRight size={12} weight="bold" className="rtl:rotate-180" />
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={dismiss}
                        data-testid="downgrade-banner-dismiss"
                        className="text-white/40 hover:text-white/70 text-[12px] transition px-2 py-1"
                        aria-label={t("downgrade.dismiss")}
                    >
                        {t("downgrade.dismiss")}
                    </button>
                </div>
            </div>
        </motion.div>
    );
}
